import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, ArrowUp, ArrowDown, Plus, Pencil, Trash2, Loader2 } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import PageHeader, { EmptyState } from "@/components/PageHeader";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";

function ActivityDialog({ editing, onClose, onDone }) {
  const [form, setForm] = useState({ name: editing?.name || "", guideline: editing?.guideline || "" });
  const [busy, setBusy] = useState(false);
  const save = async () => {
    if (!form.name.trim()) { toast.error("Activity name is required"); return; }
    setBusy(true);
    try {
      if (editing?.id) await api.put(`/thespian/activities/${editing.id}`, form);
      else await api.post("/thespian/activities", form);
      toast.success(editing?.id ? "Activity updated" : "Activity added"); onClose(); onDone();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="thespian-activity-dialog">
        <DialogHeader><DialogTitle className="font-heading">{editing?.id ? "Edit activity" : "Add activity"}</DialogTitle><DialogDescription>Members pick from this list when they claim points.</DialogDescription></DialogHeader>
        <div className="space-y-4">
          <div className="space-y-2"><Label>Activity</Label><Input value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} placeholder="e.g. Usher a community theatre show" data-testid="thespian-activity-name-input" /></div>
          <div className="space-y-2"><Label>Point guideline</Label><Input value={form.guideline} onChange={(e) => setForm((f) => ({ ...f, guideline: e.target.value }))} placeholder="e.g. 1 point = 10 hours of volunteer work" data-testid="thespian-activity-guideline-input" /></div>
        </div>
        <DialogFooter><Button className="rounded-full bg-amber-700 hover:bg-amber-800" disabled={busy} onClick={save} data-testid="thespian-activity-save">{busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Save</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ActivityRow({ a, i, last, onMove, onEdit, onDelete }) {
  return (
    <div className="flex items-center gap-3 px-4 py-3" data-testid={`thespian-activity-row-${a.id}`}>
      <span className="w-6 shrink-0 text-right text-xs font-bold tabular-nums text-slate-300">{i + 1}</span>
      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium text-slate-800">{a.name}</p>
        {a.guideline && <p className="text-xs text-amber-700">{a.guideline}</p>}
      </div>
      <div className="flex shrink-0 items-center gap-0.5">
        <Button size="icon" variant="ghost" className="h-8 w-8" disabled={i === 0} onClick={() => onMove(i, -1)} data-testid={`thespian-activity-up-${a.id}`}><ArrowUp className="h-4 w-4" /></Button>
        <Button size="icon" variant="ghost" className="h-8 w-8" disabled={last} onClick={() => onMove(i, 1)} data-testid={`thespian-activity-down-${a.id}`}><ArrowDown className="h-4 w-4" /></Button>
        <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => onEdit(a)} data-testid={`thespian-activity-edit-${a.id}`}><Pencil className="h-4 w-4" /></Button>
        <Button size="icon" variant="ghost" className="h-8 w-8 text-red-500" onClick={() => onDelete(a)} data-testid={`thespian-activity-delete-${a.id}`}><Trash2 className="h-4 w-4" /></Button>
      </div>
    </div>
  );
}

export default function ThespianActivities() {
  const navigate = useNavigate();
  const [items, setItems] = useState(null);
  const [editing, setEditing] = useState(null);
  const [deleting, setDeleting] = useState(null);

  const load = useCallback(() => { api.get("/thespian/activities").then(({ data }) => setItems(data.items || [])).catch(() => setItems([])); }, []);
  useEffect(() => { load(); }, [load]);

  const move = async (i, dir) => {
    const next = [...items];
    [next[i], next[i + dir]] = [next[i + dir], next[i]];
    setItems(next);
    try { await api.put("/thespian/activities/order", { ids: next.map((a) => a.id) }); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); load(); }
  };
  const remove = async () => {
    try { await api.delete(`/thespian/activities/${deleting.id}`); toast.success("Activity removed"); setDeleting(null); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  return (
    <div>
      <button onClick={() => navigate("/coach/thespian")} className="mb-3 inline-flex items-center gap-1 text-sm font-medium text-slate-500 hover:text-slate-800" data-testid="thespian-back-to-review"><ArrowLeft className="h-4 w-4" /> Back to Thespian Points</button>
      <PageHeader title="Thespian Activities" subtitle="The ITS activity list your members claim points against. Existing claims keep their original activity text."
        actions={<Button onClick={() => setEditing({})} className="rounded-full bg-amber-700 hover:bg-amber-800" data-testid="thespian-add-activity-btn"><Plus className="mr-2 h-4 w-4" /> Add activity</Button>} />
      {items && items.length === 0 ? <EmptyState title="No activities" description="Add activities so members can submit claims." testId="thespian-activities-empty" /> : (
        <Card className="max-w-3xl divide-y divide-slate-100 border-slate-200 shadow-sm" data-testid="thespian-activities-list">
          {(items || []).map((a, i) => <ActivityRow key={a.id} a={a} i={i} last={i === items.length - 1} onMove={move} onEdit={setEditing} onDelete={setDeleting} />)}
        </Card>
      )}
      {editing && <ActivityDialog editing={editing} onClose={() => setEditing(null)} onDone={load} />}
      <ConfirmDialog open={!!deleting} onOpenChange={(o) => !o && setDeleting(null)} title="Remove activity?"
        description={deleting ? `“${deleting.name}” will no longer be available for new claims. Past claims are unaffected.` : ""}
        confirmText="Remove" destructive onConfirm={remove} testId="thespian-activity-delete-confirm" />
    </div>
  );
}
